import { forwardRef, useState, type HTMLAttributes } from 'react';
import { cn } from '../../../utils';
import {
  FileText,
  FileSpreadsheet,
  FileCode,
  FileImage,
  File,
  X,
  Download,
  Loader2,
  AlertCircle,
  CheckCircle2,
  RotateCcw,
} from 'lucide-react';

// ============================================================================
// File Attachment - Display file attachments in chat messages
// ============================================================================

export type FileStatus = 'pending' | 'uploading' | 'processing' | 'complete' | 'error';

export interface FileAttachmentFile {
  id: string;
  name: string;
  size: number;
  type?: string;
  status?: FileStatus;
  progress?: number;
  error?: string;
  url?: string;
}

export interface FileAttachmentProps extends HTMLAttributes<HTMLDivElement> {
  /** Single file or array of files */
  files: FileAttachmentFile | FileAttachmentFile[];
  /** Show remove button for each file */
  onRemove?: (fileId: string) => void;
  /** Show download button for completed files */
  onDownload?: (fileId: string) => void;
  /** Retry callback for files with error status */
  onRetry?: (fileId: string) => void;
  /** Compact mode for inline display */
  compact?: boolean;
  /** Maximum files to show before collapsing */
  maxVisible?: number;
}

// File type detection based on extension
const getFileType = (filename: string): 'spreadsheet' | 'code' | 'image' | 'document' | 'other' => {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  if (['xlsx', 'xls', 'csv', 'parquet'].includes(ext)) return 'spreadsheet';
  if (['json', 'js', 'ts', 'tsx', 'jsx', 'py', 'go', 'html', 'css', 'xml', 'yaml', 'yml'].includes(ext)) return 'code';
  if (['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'ico'].includes(ext)) return 'image';
  if (['pdf', 'doc', 'docx', 'txt', 'md', 'rtf'].includes(ext)) return 'document';
  return 'other';
};

// Icon mapping for file types
const FileIcon = ({ filename, className }: { filename: string; className?: string }) => {
  const type = getFileType(filename);
  const iconClass = cn('h-4 w-4', className);

  switch (type) {
    case 'spreadsheet':
      return <FileSpreadsheet className={cn(iconClass, 'text-emerald-500')} />;
    case 'code':
      return <FileCode className={cn(iconClass, 'text-purple-500')} />;
    case 'image':
      return <FileImage className={cn(iconClass, 'text-sky-500')} />;
    case 'document':
      return <FileText className={cn(iconClass, 'text-amber-500')} />;
    default:
      return <File className={cn(iconClass, 'text-slate-400')} />;
  }
};

// Format file size
const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

// Status indicator
const StatusIndicator = ({ status, progress }: { status: FileStatus; progress?: number }) => {
  switch (status) {
    case 'uploading':
      return (
        <div className="flex items-center gap-1.5">
          <Loader2 className="h-3 w-3 text-sky-500 animate-spin" />
          {progress !== undefined && (
            <span className="text-xs text-slate-500 tabular-nums">{progress}%</span>
          )}
        </div>
      );
    case 'processing':
      return <Loader2 className="h-3 w-3 text-amber-500 animate-spin" />;
    case 'complete':
      return <CheckCircle2 className="h-3 w-3 text-emerald-500" />;
    case 'error':
      return <AlertCircle className="h-3 w-3 text-rose-500" />;
    default:
      return null;
  }
};

// Single file item
interface FileItemProps {
  file: FileAttachmentFile;
  compact?: boolean;
  onRemove?: (fileId: string) => void;
  onDownload?: (fileId: string) => void;
  onRetry?: (fileId: string) => void;
}

const FileItem = ({ file, compact, onRemove, onDownload, onRetry }: FileItemProps) => {
  const status = file.status || 'complete';
  const isComplete = status === 'complete';
  const isError = status === 'error';
  const isUploading = status === 'uploading';

  return (
    <div
      className={cn(
        'group flex items-center gap-2 rounded-lg border transition-colors',
        compact ? 'px-2 py-1' : 'px-3 py-2',
        isError
          ? 'border-rose-200 bg-rose-50 dark:border-rose-800 dark:bg-rose-950/30'
          : 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/50',
        isComplete && 'hover:border-slate-300 dark:hover:border-slate-600'
      )}
    >
      <FileIcon filename={file.name} />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'text-sm truncate',
              isError ? 'text-rose-700 dark:text-rose-300' : 'text-slate-700 dark:text-slate-300'
            )}
            title={file.name}
          >
            {file.name}
          </span>
          {!compact && <StatusIndicator status={status} progress={file.progress} />}
        </div>

        {!compact && (
          <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
            <span>{formatSize(file.size)}</span>
            {isError && file.error && (
              <span className="text-rose-600 dark:text-rose-400">{file.error}</span>
            )}
          </div>
        )}

        {/* Progress bar for uploading */}
        {isUploading && file.progress !== undefined && !compact && (
          <div className="mt-1.5 h-1 w-full rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
            <div
              className="h-full bg-sky-500 transition-all duration-300"
              style={{ width: `${file.progress}%` }}
            />
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
        {isError && onRetry && (
          <button
            type="button"
            onClick={() => onRetry(file.id)}
            className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-sky-500 hover:text-sky-600 dark:text-sky-400 dark:hover:text-sky-300"
            title="Retry"
          >
            <RotateCcw className="h-3.5 w-3.5" />
          </button>
        )}
        {isComplete && onDownload && (
          <button
            type="button"
            onClick={() => onDownload(file.id)}
            className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
            title="Download"
          >
            <Download className="h-3.5 w-3.5" />
          </button>
        )}
        {onRemove && (
          <button
            type="button"
            onClick={() => onRemove(file.id)}
            className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500 hover:text-rose-500 dark:text-slate-400 dark:hover:text-rose-400"
            title="Remove"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};

export const FileAttachment = forwardRef<HTMLDivElement, FileAttachmentProps>(
  ({ className, files, onRemove, onDownload, onRetry, compact = false, maxVisible = 5, ...props }, ref) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const fileArray = Array.isArray(files) ? files : [files];
    const visibleFiles = (maxVisible && !isExpanded) ? fileArray.slice(0, maxVisible) : fileArray;
    const hiddenCount = isExpanded ? 0 : fileArray.length - visibleFiles.length;

    if (fileArray.length === 0) return null;

    return (
      <div
        ref={ref}
        className={cn(
          'flex flex-wrap gap-2',
          compact && 'gap-1',
          className
        )}
        {...props}
      >
        {visibleFiles.map((file) => (
          <FileItem
            key={file.id}
            file={file}
            compact={compact}
            onRemove={onRemove}
            onDownload={onDownload}
            onRetry={onRetry}
          />
        ))}
        {hiddenCount > 0 && (
          <button
            type="button"
            onClick={() => setIsExpanded(true)}
            className={cn(
              'flex items-center justify-center rounded-lg border border-slate-200 bg-slate-50',
              'dark:border-slate-700 dark:bg-slate-800/50',
              'hover:border-slate-300 hover:bg-slate-100 dark:hover:border-slate-600 dark:hover:bg-slate-800',
              'transition-colors cursor-pointer',
              compact ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm',
              'text-slate-500 dark:text-slate-400'
            )}
          >
            +{hiddenCount} more
          </button>
        )}
      </div>
    );
  }
);
FileAttachment.displayName = 'FileAttachment';

// ============================================================================
// File Attachment List - Timeline-integrated file display
// ============================================================================

export interface FileAttachmentListProps extends HTMLAttributes<HTMLDivElement> {
  files: FileAttachmentFile[];
  onRemove?: (fileId: string) => void;
  onDownload?: (fileId: string) => void;
  /** Retry callback for files with error status */
  onRetry?: (fileId: string) => void;
  /** Title shown above the file list */
  title?: string;
  /** Show in a card container */
  showCard?: boolean;
}

export const FileAttachmentList = forwardRef<HTMLDivElement, FileAttachmentListProps>(
  ({ className, files, onRemove, onDownload, onRetry, title, showCard = false, ...props }, ref) => {
    if (files.length === 0) return null;

    const content = (
      <>
        {title && (
          <div className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">
            {title}
          </div>
        )}
        <div className="space-y-1.5">
          {files.map((file) => (
            <FileItem
              key={file.id}
              file={file}
              onRemove={onRemove}
              onDownload={onDownload}
              onRetry={onRetry}
            />
          ))}
        </div>
      </>
    );

    if (showCard) {
      return (
        <div
          ref={ref}
          className={cn(
            'rounded-lg border border-slate-200 bg-white p-3',
            'dark:border-slate-700 dark:bg-slate-900',
            className
          )}
          {...props}
        >
          {content}
        </div>
      );
    }

    return (
      <div ref={ref} className={className} {...props}>
        {content}
      </div>
    );
  }
);
FileAttachmentList.displayName = 'FileAttachmentList';
